/* ==========================================================================
   Studio Obliq — "RD" section behaviours
   All bindings are scoped to a root element and are idempotent, so the theme
   editor can re-run them on shopify:section:load without stacking listeners.
   ========================================================================== */
(function () {
  'use strict';

  var BOUND = 'rdBound';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* --- scroll progress indicator ---------------------------------------- */
  function bindBar(root) {
    each(root.querySelectorAll('[data-rd-bar]'), function (bar) {
      var fill = bar.querySelector('[data-rd-bar-fill]');
      if (!fill) return;

      function update() {
        // The visible shelf may change (tabbed panels), so resolve it each time.
        var shelf = null;
        each(root.querySelectorAll('[data-rd-shelf="' + bar.getAttribute('data-rd-bar') + '"]'), function (el) {
          if (!shelf && el.offsetParent !== null) shelf = el;
        });
        if (!shelf) return;

        var track = bar.clientWidth;
        var ratio = shelf.clientWidth / Math.max(1, shelf.scrollWidth);
        var width = Math.max(0.15, Math.min(1, ratio)) * track;
        var max = Math.max(1, shelf.scrollWidth - shelf.clientWidth);
        var x = (shelf.scrollLeft / max) * (track - width);

        fill.style.width = width + 'px';
        fill.style.transform = 'translateX(' + x + 'px)';
        bar.style.visibility = ratio >= 1 ? 'hidden' : '';
      }

      each(root.querySelectorAll('[data-rd-shelf="' + bar.getAttribute('data-rd-bar') + '"]'), function (shelf) {
        if (shelf[BOUND]) return;
        shelf[BOUND] = true;
        shelf.addEventListener('scroll', update, { passive: true });
      });

      window.addEventListener('resize', update);
      root.addEventListener('rd:refresh', update);
      update();
    });
  }

  /* --- programmatic slide ------------------------------------------------ */
  var SNAP_TIMER = 'rdSnapTimer';

  // A mandatory snap container cancels a programmatic smooth scroll, so
  // snapping is suspended for the duration of the animation and restored once
  // it settles. Swipe snapping is unaffected.
  function glide(track, left) {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      track.scrollTo({ left: left, behavior: 'auto' });
      return;
    }
    var before = track.scrollLeft;
    track.style.scrollSnapType = 'none';
    track.scrollTo({ left: left, behavior: 'smooth' });

    // Smooth scrolling is unavailable in some engines (and is cancelled
    // outright by some snap implementations). If nothing has moved shortly
    // after the call, land the slide instantly so navigation never dead-ends.
    setTimeout(function () {
      if (track.scrollLeft === before && Math.abs(left - before) > 2) {
        track.scrollLeft = left;
      }
    }, 120);

    clearTimeout(track[SNAP_TIMER]);
    track[SNAP_TIMER] = setTimeout(function () {
      track.style.scrollSnapType = '';
    }, 500);
  }

  /* --- slider: pips, clickable dots and arrows ---------------------------- */
  function bindDots(root) {
    each(root.querySelectorAll('[data-rd-track]'), function (track) {
      if (track[BOUND]) return;
      track[BOUND] = true;

      var key = track.getAttribute('data-rd-track');
      var dots = root.querySelector('[data-rd-dots="' + key + '"]');
      var pips = dots ? dots.children : [];

      function current() {
        return Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      }

      function goTo(i) {
        var max = track.children.length - 1;
        var target = Math.max(0, Math.min(max, i));
        glide(track, target * track.clientWidth);
      }

      // Anything else keyed to this track follows the active slide too, e.g.
      // Spotlight's copy panel: [data-rd-slide-sync="key"] > one child per slide.
      var synced = root.querySelectorAll('[data-rd-slide-sync="' + key + '"]');

      track.addEventListener(
        'scroll',
        function () {
          var i = current();
          each(pips, function (pip, k) {
            pip.classList.toggle('is-on', k === i);
            if (pip.tagName === 'BUTTON') pip.setAttribute('aria-current', k === i ? 'true' : 'false');
          });
          each(synced, function (group) {
            each(group.children, function (el, k) {
              var on = k === i;
              el.classList.toggle('is-on', on);
              el.setAttribute('aria-hidden', on ? 'false' : 'true');
            });
          });
        },
        { passive: true }
      );

      each(pips, function (pip) {
        if (pip.tagName !== 'BUTTON') return;
        pip.addEventListener('click', function () {
          goTo(parseInt(pip.getAttribute('data-rd-slide-to'), 10) || 0);
        });
      });

      each(root.querySelectorAll('[data-rd-slide-prev="' + key + '"]'), function (btn) {
        btn.addEventListener('click', function () { goTo(current() - 1); });
      });
      each(root.querySelectorAll('[data-rd-slide-next="' + key + '"]'), function (btn) {
        btn.addEventListener('click', function () { goTo(current() + 1); });
      });
    });
  }

  /* --- tabbed panels ----------------------------------------------------- */
  function bindTabs(root) {
    each(root.querySelectorAll('[data-rd-tabs]'), function (group) {
      if (group[BOUND]) return;
      group[BOUND] = true;

      var buttons = group.querySelectorAll('[data-rd-tab]');
      var cta = root.querySelector('[data-rd-tabs-cta]');

      // Point the "Shop all" button at the open tab's collection.
      function syncCta(btn) {
        if (!cta) return;
        var url = btn.getAttribute('data-rd-tab-url');
        if (!url) url = cta.getAttribute('data-rd-cta-fallback') || '';
        if (url) {
          cta.setAttribute('href', url);
          cta.removeAttribute('aria-disabled');
        } else {
          cta.removeAttribute('href');
          cta.setAttribute('aria-disabled', 'true');
        }
      }

      each(buttons, function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-rd-tab');

          each(buttons, function (b) {
            var on = b === btn;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.setAttribute('tabindex', on ? '0' : '-1');
          });

          each(root.querySelectorAll('[data-rd-panel]'), function (panel) {
            var on = panel.getAttribute('data-rd-panel') === target;
            panel.hidden = !on;
          });

          syncCta(btn);
          root.dispatchEvent(new CustomEvent('rd:refresh'));
        });

        // Roving focus, per the WAI-ARIA tabs pattern.
        btn.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var list = Array.prototype.slice.call(buttons);
          var i = list.indexOf(btn);
          var next = list[(i + (e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
          next.focus();
          next.click();
        });
      });
    });
  }

  /* --- materials list -> featured image ---------------------------------- */
  function bindSwitcher(root) {
    each(root.querySelectorAll('[data-rd-switcher]'), function (group) {
      if (group[BOUND]) return;
      group[BOUND] = true;

      var items = group.querySelectorAll('[data-rd-switch]');
      var panels = root.querySelectorAll('[data-rd-switch-panel]');
      var hover = group.getAttribute('data-rd-switcher') === 'hover';

      // The images are lazy so they stay out of the first paint. Pull all of
      // them in as the section approaches, ahead of the first click or hover.
      function preload() {
        each(panels, function (panel) {
          if (panel.tagName === 'IMG') panel.loading = 'eager';
        });
      }
      if ('IntersectionObserver' in window) {
        var near = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          near.disconnect();
          preload();
        }, { rootMargin: '600px 0px' });
        near.observe(group);
      } else {
        preload();
      }

      // Every image is already in the DOM, so activating one is a class toggle
      // rather than a src swap - no network request, no flash of empty space.
      function activate(item) {
        var to = item.getAttribute('data-rd-switch-to');
        each(items, function (el) {
          var on = el === item;
          el.classList.toggle('is-on', on);
          el.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        each(panels, function (panel) {
          var on = panel.getAttribute('data-rd-switch-panel') === to;
          panel.classList.toggle('is-on', on);
          if (on) panel.removeAttribute('aria-hidden');
          else panel.setAttribute('aria-hidden', 'true');
        });
      }

      each(items, function (item) {
        item.addEventListener('click', function (e) {
          // The row is a link to the collection; only intercept the swap
          // affordance, never the shop link itself.
          if (e.target.closest('[data-rd-switch-through]')) return;
          e.preventDefault();
          activate(item);
        });

        if (hover) {
          item.addEventListener('mouseenter', function () {
            if (window.matchMedia('(min-width: 990px)').matches) activate(item);
          });
        }
      });
    });
  }

  /* --- marquee: only animate when the content actually overflows ---------- */
  function bindMarquee(root) {
    each(root.querySelectorAll('[data-rd-marquee]'), function (band) {
      var row = band.querySelector('[data-rd-marquee-row]');
      if (!row) return;

      function update() {
        var sets = row.querySelectorAll('[data-rd-marquee-set]');
        if (!sets.length) return;
        var overflows = sets[0].scrollWidth > band.clientWidth;
        var always = band.getAttribute('data-rd-marquee') === 'always';
        band.classList.toggle('is-animating', always || overflows);
      }

      window.addEventListener('resize', update);
      update();
    });
  }

  /* --- shelf stepping ------------------------------------------------------ */
  // Start offset of every card relative to the shelf's scroll origin.
  function cardStarts(shelf) {
    var origin = shelf.getBoundingClientRect().left - shelf.scrollLeft;
    return Array.prototype.map.call(shelf.children, function (c) {
      return c.getBoundingClientRect().left - origin;
    });
  }

  // Scroll position one card forward (+1) or back (-1) from where we are, or
  // null when there is no such card. Clamped to the scrollable range.
  function stepTarget(shelf, dir) {
    var starts = cardStarts(shelf);
    var max = shelf.scrollWidth - shelf.clientWidth;
    var x = shelf.scrollLeft;
    var i;
    if (dir > 0) {
      for (i = 0; i < starts.length; i++) {
        if (starts[i] > x + 1) return Math.min(starts[i], max);
      }
      return null;
    }
    for (i = starts.length - 1; i >= 0; i--) {
      if (starts[i] < x - 1) return Math.max(0, starts[i]);
    }
    return null;
  }

  /* --- autoplay: advance a shelf one card at a time ----------------------- */
  function bindAutoplay(root) {
    each(root.querySelectorAll('[data-rd-autoplay]'), function (shelf) {
      if (shelf.rdAutoplayBound) return;
      shelf.rdAutoplayBound = true;

      var interval = parseInt(shelf.getAttribute('data-rd-autoplay'), 10) || 5000;
      var media = shelf.getAttribute('data-rd-autoplay-media');
      var mq = media ? window.matchMedia(media) : null;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

      var timer = null;
      var paused = false;   // hover / focus / pointer down
      var visible = true;   // on screen and tab is in the foreground
      var settling = 0;     // ignore the scroll events our own glide produces

      function allowed() {
        if (reduce.matches) return false;
        if (mq && !mq.matches) return false;
        // Nothing to slide when every card already fits.
        return shelf.scrollWidth - shelf.clientWidth > 2;
      }

      function next() {
        // Re-check every tick: a viewport change that slipped past the resize
        // and media-query listeners must never leave a slider running on mobile.
        if (!allowed()) {
          stop();
          return;
        }
        var atEnd = shelf.scrollLeft + shelf.clientWidth >= shelf.scrollWidth - 2;
        var left = atEnd ? 0 : stepTarget(shelf, 1);
        if (left === null) left = 0;
        settling = Date.now() + 700;
        glide(shelf, left);
      }

      function stop() {
        clearInterval(timer);
        timer = null;
      }

      function start() {
        stop();
        if (paused || !visible || !allowed()) return;
        timer = setInterval(next, interval);
      }

      // A manual swipe or drag resets the clock so the slider never fights the
      // visitor; our own glide is ignored.
      var scrollTimer;
      shelf.addEventListener(
        'scroll',
        function () {
          if (Date.now() < settling) return;
          stop();
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(start, 250);
        },
        { passive: true }
      );

      shelf.addEventListener('mouseenter', function () { paused = true; stop(); });
      shelf.addEventListener('mouseleave', function () { paused = false; start(); });
      shelf.addEventListener('focusin', function () { paused = true; stop(); });
      shelf.addEventListener('focusout', function () { paused = false; start(); });
      shelf.addEventListener('pointerdown', function () { paused = true; stop(); }, { passive: true });
      shelf.addEventListener('pointerup', function () { paused = false; start(); }, { passive: true });
      shelf.addEventListener('pointercancel', function () { paused = false; start(); }, { passive: true });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          var onScreen = entries[0].isIntersecting;
          visible = onScreen && !document.hidden;
          start();
        }, { threshold: 0.25 });
        io.observe(shelf);
      }
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { visible = false; stop(); } else { visible = true; start(); }
      });

      window.addEventListener('resize', start);
      if (mq) mq.addEventListener('change', start);
      reduce.addEventListener('change', start);
      start();
    });
  }

  /* --- shelf arrows -------------------------------------------------------- */
  // <button data-rd-shelf-prev="key"> / <button data-rd-shelf-next="key"> step
  // the matching [data-rd-shelf="key"] one card. Disabled at either end and
  // hidden altogether when every card already fits.
  function bindShelfArrows(root) {
    each(root.querySelectorAll('[data-rd-shelf]'), function (shelf) {
      var key = shelf.getAttribute('data-rd-shelf');
      var prev = root.querySelectorAll('[data-rd-shelf-prev="' + key + '"]');
      var next = root.querySelectorAll('[data-rd-shelf-next="' + key + '"]');
      if (!prev.length && !next.length) return;
      if (shelf.rdArrowsBound) return;
      shelf.rdArrowsBound = true;

      function setState(btns, disabled) {
        each(btns, function (b) {
          b.disabled = disabled;
          b.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        });
      }

      function update() {
        var max = shelf.scrollWidth - shelf.clientWidth;
        var fits = max <= 2;
        each(prev, function (b) { b.closest('[data-rd-arrows]') ? (b.closest('[data-rd-arrows]').hidden = fits) : (b.hidden = fits); });
        each(next, function (b) { b.closest('[data-rd-arrows]') ? (b.closest('[data-rd-arrows]').hidden = fits) : (b.hidden = fits); });
        setState(prev, shelf.scrollLeft <= 1);
        setState(next, shelf.scrollLeft >= max - 1);
      }

      function step(dir) {
        var left = stepTarget(shelf, dir);
        if (left === null) return;
        glide(shelf, left);
        // Scroll events normally keep the buttons in step; this covers engines
        // that defer them (background tabs) so an arrow never reads stale.
        setTimeout(update, 650);
      }

      each(prev, function (b) { b.addEventListener('click', function () { step(-1); }); });
      each(next, function (b) { b.addEventListener('click', function () { step(1); }); });

      shelf.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      root.addEventListener('rd:refresh', update);
      update();
    });
  }

  /* --- product-card image sliders ----------------------------------------- */
  // The theme initialises the card Swipers from product-card-slider.js, which
  // only its own collection sections load, so on a page built from RD sections
  // the arrows do nothing. Initialise them here, scoped to this section and to
  // cards nothing else has claimed. Cards inside a hidden tab panel start at
  // zero width; `observer` re-measures them the moment the panel is shown.
  function mountCardSlider(el) {
    if (el.swiper || el.classList.contains('swiper-initialized')) return;
    new window.Swiper(el, {
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: el.querySelector('.swiper-pagination'),
        clickable: true,
      },
      observer: true,
      observeParents: true,
      watchOverflow: true,
    });
  }

  // Run after the browser has painted, so a tap shows its result first and
  // the follow-up work does not count against the interaction.
  function afterPaint(fn) {
    requestAnimationFrame(function () {
      setTimeout(fn, 0);
    });
  }

  function bindCardSliders(root) {
    if (typeof window.Swiper !== 'function') return;

    // Building every card's Swiper up front blocks the main thread while the
    // page is loading, which is when first taps land. Each one is built as its
    // card nears the viewport instead; cards in a hidden tab panel never
    // intersect, so they wait until their tab is opened.
    if ('IntersectionObserver' in window) {
      if (!root.rdCardObserver) {
        root.rdCardObserver = new IntersectionObserver(function (entries) {
          each(entries, function (entry) {
            if (!entry.isIntersecting) return;
            root.rdCardObserver.unobserve(entry.target);
            mountCardSlider(entry.target);
          });
        }, { rootMargin: '300px' });
      }
      each(root.querySelectorAll('.product-card-js'), function (el) {
        if (el.swiper || el.rdCardObserved) return;
        el.rdCardObserved = true;
        root.rdCardObserver.observe(el);
      });
    } else {
      each(root.querySelectorAll('.product-card-js'), mountCardSlider);
    }

    if (root.rdCardSlidersBound) return;
    root.rdCardSlidersBound = true;
    // Tab switches reveal a panel that was display:none at init time.
    root.addEventListener('rd:refresh', function () {
      afterPaint(function () {
        each(root.querySelectorAll('.product-card-js'), function (el) {
          if (el.swiper && el.offsetParent !== null) el.swiper.update();
        });
      });
    });
  }

  function init(scope) {
    each((scope || document).querySelectorAll('[data-rd-section]'), function (root) {
      bindBar(root);
      bindDots(root);
      bindTabs(root);
      bindSwitcher(root);
      bindMarquee(root);
      bindAutoplay(root);
      bindShelfArrows(root);
      bindCardSliders(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
